import { Clock, Calendar, User, Building2 } from 'lucide-react'
import { cn, formatDate, formatDuration, daysUntil, getDeadlineColor } from '@/lib/utils'
import { Card } from '@/components/ui'
import { TaskStatusBadge } from './TaskStatusBadge'
import type { Task, TaskPriority } from '@/api'

const priorityBorderColors: Record<TaskPriority, string> = {
  low: 'border-l-slate-500',
  medium: 'border-l-blue-500',
  high: 'border-l-amber-500',
  urgent: 'border-l-red-500',
}

const priorityLabels: Record<TaskPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
}

interface TaskCardProps {
  task: Task
  onClick?: () => void
  selected?: boolean
}

export function TaskCard({ task, onClick, selected }: TaskCardProps) {
  const daysRemaining = task.due_date ? daysUntil(task.due_date) : null

  return (
    <Card
      className={cn(
        'border-l-4 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-border/80',
        priorityBorderColors[task.priority],
        selected && 'ring-1 ring-primary/50 bg-primary/5 shadow-md'
      )}
      onClick={onClick}
    >
      <div className="p-4 space-y-3">
        {/* Header: Title + Status */}
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-medium text-sm leading-tight line-clamp-2">
            {task.title}
          </h3>
          <TaskStatusBadge status={task.status} />
        </div>

        {/* Priority tag */}
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'text-xs font-mono px-1.5 py-0.5 rounded',
              task.priority === 'urgent' && 'bg-red-500/10 text-red-600 dark:text-red-400',
              task.priority === 'high' && 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
              task.priority === 'medium' && 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
              task.priority === 'low' && 'bg-muted text-muted-foreground'
            )}
          >
            {priorityLabels[task.priority]}
          </span>
          {task.statutory_type && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-500 dark:text-violet-400">
              {task.statutory_type}
            </span>
          )}
        </div>

        {/* Meta info */}
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          {/* Due date */}
          {task.due_date && (
            <div className={cn('flex items-center gap-1', getDeadlineColor(daysRemaining!))}>
              <Calendar className="h-3.5 w-3.5" />
              <span>
                {daysRemaining !== null && daysRemaining < 0
                  ? `Overdue by ${Math.abs(daysRemaining)}d`
                  : daysRemaining === 0
                  ? 'Due today'
                  : daysRemaining === 1
                  ? 'Due tomorrow'
                  : formatDate(task.due_date)}
              </span>
            </div>
          )}

          {/* Time logged */}
          {task.time_logged_minutes > 0 && (
            <div className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              <span>{formatDuration(task.time_logged_minutes)}</span>
            </div>
          )}

          {/* Assignee */}
          {task.assignee_name && (
            <div className="flex items-center gap-1">
              <User className="h-3.5 w-3.5" />
              <span className="truncate max-w-[100px]">{task.assignee_name}</span>
            </div>
          )}

          {/* Client */}
          {task.client_name && (
            <div className="flex items-center gap-1">
              <Building2 className="h-3.5 w-3.5" />
              <span className="truncate max-w-[100px]">{task.client_name}</span>
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}
